import {
  CUMULATIVE_HOURS_PER_MONTH,
  HOURS_PER_YEAR,
  MONTHS,
} from "../savings/time"

export type RenormalizeTypicalToMonthlyKwhInput = {
  typicalHourlyProportions: number[]
  monthlyKwh: number[]
}

const assertTypicalYear = (typicalHourlyProportions: number[]): void => {
  if (typicalHourlyProportions.length !== HOURS_PER_YEAR) {
    throw new Error(`typicalHourlyProportions must have ${HOURS_PER_YEAR} hours`)
  }

  if (typicalHourlyProportions.some((value) => !Number.isFinite(value))) {
    throw new Error(`typicalHourlyProportions must be finite`)
  }
}

const monthSlice = (
  hourly: number[],
  month: number,
): { start: number; hours: number[] } => {
  const start = CUMULATIVE_HOURS_PER_MONTH[month]
  const end = CUMULATIVE_HOURS_PER_MONTH[month + 1]

  return { start, hours: hourly.slice(start, end) }
}

const applyExactTotal = (hours: number[], total: number): number[] => {
  if (hours.length === 0) {
    return hours
  }

  const scaledSum = hours.reduce((sum, value) => sum + value, 0)
  const residual = total - scaledSum
  const lastIndex = hours.length - 1

  return hours.map((value, index) =>
    index === lastIndex ? value + residual : value,
  )
}

export const renormalizeTypicalToMonthlyKwh = ({
  typicalHourlyProportions,
  monthlyKwh,
}: RenormalizeTypicalToMonthlyKwhInput): number[] => {
  assertTypicalYear(typicalHourlyProportions)

  if (monthlyKwh.length !== MONTHS.length) {
    throw new Error(`monthlyKwh must have ${MONTHS.length} months`)
  }

  if (monthlyKwh.some((value) => !Number.isFinite(value))) {
    throw new Error(`monthlyKwh must be finite`)
  }

  const hourly = new Array<number>(HOURS_PER_YEAR).fill(0)

  MONTHS.forEach((_, month) => {
    const { start, hours } = monthSlice(typicalHourlyProportions, month)
    const monthProportionSum = hours.reduce((sum, value) => sum + value, 0)
    const target = monthlyKwh[month]
    const scaled =
      monthProportionSum === 0
        ? hours.map(() => target / hours.length)
        : hours.map((value) => (value / monthProportionSum) * target)

    applyExactTotal(scaled, target).forEach((value, index) => {
      hourly[start + index] = value
    })
  })

  return hourly
}

export type ScaleTypicalToAnnualKwhInput = {
  typicalHourlyProportions: number[]
  annualKwh: number
}

export const scaleTypicalToAnnualKwh = ({
  typicalHourlyProportions,
  annualKwh,
}: ScaleTypicalToAnnualKwhInput): number[] => {
  assertTypicalYear(typicalHourlyProportions)

  if (!Number.isFinite(annualKwh)) {
    throw new Error(`annualKwh must be finite`)
  }

  const typicalSum = typicalHourlyProportions.reduce(
    (sum, value) => sum + value,
    0,
  )
  if (typicalSum === 0) {
    throw new Error(`typicalHourlyProportions must have positive mass`)
  }

  const scaled = typicalHourlyProportions.map(
    (value) => (value / 100) * annualKwh,
  )

  return applyExactTotal(scaled, annualKwh)
}

export type ShapeAnnualToMonthlyTypicalInput = {
  monthlyWeights: number[]
  annualKwh: number
}

const isUsableMonthlyWeights = (monthlyWeights: number[]): boolean =>
  monthlyWeights.length === MONTHS.length &&
  monthlyWeights.every((value) => Number.isFinite(value) && value >= 0) &&
  monthlyWeights.reduce((sum, value) => sum + value, 0) > 0

export const shapeAnnualToMonthlyTypical = ({
  monthlyWeights,
  annualKwh,
}: ShapeAnnualToMonthlyTypicalInput): number[] => {
  if (!Number.isFinite(annualKwh)) {
    throw new Error(`annualKwh must be finite`)
  }

  if (!isUsableMonthlyWeights(monthlyWeights)) {
    const uniform = Array.from(
      { length: MONTHS.length },
      () => annualKwh / MONTHS.length,
    )

    return applyExactTotal(uniform, annualKwh)
  }

  const weightSum = monthlyWeights.reduce((sum, value) => sum + value, 0)
  const scaled = monthlyWeights.map((value) => (value / weightSum) * annualKwh)

  return applyExactTotal(scaled, annualKwh)
}

export const monthlyWeightsFromHourlyProportions = (
  typicalHourlyProportions: number[],
): number[] => {
  assertTypicalYear(typicalHourlyProportions)

  return MONTHS.map((_, month) =>
    monthSlice(typicalHourlyProportions, month).hours.reduce(
      (sum, value) => sum + value,
      0,
    ),
  )
}

export type FlatFallbackMonthlyInput = {
  monthlyKwh: number[]
}

export type FlatFallbackAnnualInput = {
  annualKwh: number
}

export type FlatFallbackInput =
  | FlatFallbackAnnualInput
  | FlatFallbackMonthlyInput

const isMonthlyFlatFallback = (
  input: FlatFallbackInput,
): input is FlatFallbackMonthlyInput => `monthlyKwh` in input

export const buildFlatFallbackHourlyKwh = (
  input: FlatFallbackInput,
): number[] => {
  if (isMonthlyFlatFallback(input)) {
    if (input.monthlyKwh.length !== MONTHS.length) {
      throw new Error(`monthlyKwh must have ${MONTHS.length} months`)
    }

    if (input.monthlyKwh.some((value) => !Number.isFinite(value))) {
      throw new Error(`monthlyKwh must be finite`)
    }

    const hourly = new Array<number>(HOURS_PER_YEAR).fill(0)

    MONTHS.forEach((_, month) => {
      const start = CUMULATIVE_HOURS_PER_MONTH[month]
      const end = CUMULATIVE_HOURS_PER_MONTH[month + 1]
      const hourCount = end - start
      const target = input.monthlyKwh[month]
      const uniform = Array.from({ length: hourCount }, () => target / hourCount)

      applyExactTotal(uniform, target).forEach((value, index) => {
        hourly[start + index] = value
      })
    })

    return hourly
  }

  if (!Number.isFinite(input.annualKwh)) {
    throw new Error(`annualKwh must be finite`)
  }

  const uniform = Array.from(
    { length: HOURS_PER_YEAR },
    () => input.annualKwh / HOURS_PER_YEAR,
  )

  return applyExactTotal(uniform, input.annualKwh)
}
